import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress } from '@mui/material';
import { PAPER_TABS } from '../../data/common';
import { vocabularyApi } from '../../api/global';
import setIcon from '../../assets/images/ic_set_white.svg';
import VocaTestPaperSetBtns from './VocaTestPaperSetBtns';
import Header from './TestPaperRows/Header';
import ATypeTestRows from './TestPaperRows/ATypeTestRow';
import BTypeTestRows from './TestPaperRows/BTypeTestRow';
import CTypeTestRows from './TestPaperRows/CTypeTestRow';
import DTypeTestRows from './TestPaperRows/DTypeTestRow';
import ETypeTestRows from './TestPaperRows/ETypeTestRow';
import FTypeTestRows from './TestPaperRows/FTypeTestRow';
import GTypeTestRows from './TestPaperRows/GTypeTestRow';
import HTypeTestRows from './TestPaperRows/HTypeTestRow';
import ITypeTestRows from './TestPaperRows/ITypeTestRow';
import JTypeTestRows from './TestPaperRows/JTypeTestRow';
import KTypeTestRows from './TestPaperRows/KTypeTestRow';
import LTypeTestRows from './TestPaperRows/LTypeTestRow';
import MTypeTestRows from './TestPaperRows/MTypeTestRow';
import CommonPaperVoca from './TestPaperRows/CommonPaperVoca';

const iframeWidth = 824;
const iframeHeight = 910;

function VocaTestPaper({
  tab,
  setTab,
  config,
  generatedPaper = [],
  hideTestPaper,
  setHideTestPaper,
  saveFinalVocaList,
  saveBtnDisabled,
  pdfUrl = null,
  setPdfUrl,
  setPdfBlob,
  setPrintSetOpen,
  setIsFolding,
  printSetTrigger,
  commonPaperVocaList,
  isLoadMode,
  printSetOpen,
}) {
  const contentRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [viewModeChanging, setViewModeChanging] = useState(false);
  const [, setZoomLevel] = useState(100);

  // PDF 생성
  const handleGeneratePdf = useCallback(async () => {
    const htmlContent = contentRef?.current?.outerHTML;
    const headStr = `
      <style>
        @font-face {
          font-family: 'Pretendard';
          src: url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.woff2') format('woff2');
          font-weight: 400;
          font-style: normal;
        }
        body {
          font-family: 'Pretendard', Arial, sans-serif;
        }
        body::before {
        content: "CEDU";
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-family: 'Pretendard', Arial, sans-serif;
        font-size: 90px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.1);
        z-index: 9999;
        pointer-events: none;
        user-select: none;
        white-space: nowrap;
      }
      </style>
    `;
    const finalParams = headStr + htmlContent;
    try {
      const response = await vocabularyApi.pdfGenerate(finalParams);
      console.warn('pdfGenerate response:', response.status);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfBlob(blob);
      if (url) {
        setLoading(false);
        setViewModeChanging(false);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setLoading(false);
      setViewModeChanging(false);
    }
  }, [contentRef, setPdfBlob, setPdfUrl]);

  const detectZoomLevel = () => {
    const zoom = (window.devicePixelRatio * 100).toFixed(0) / 2; // 정확한 확대/축소 비율 계산
    setZoomLevel(zoom);
  };

  // 초기 확대/축소 비율 설정
  useEffect(() => {
    detectZoomLevel();
    // 윈도우 크기 및 줌 변경 시 확대/축소 비율 업데이트
    window.addEventListener('resize', detectZoomLevel);
    window.addEventListener('zoom', detectZoomLevel); // 브라우저에서 zoom 이벤트가 지원되는 경우
    return () => {
      window.removeEventListener('resize', detectZoomLevel);
      window.removeEventListener('zoom', detectZoomLevel);
    };
  }, []);

  // 시험지 PrintSet의 변경감지후, 미리보기 업데이트
  useEffect(() => {
    if (generatedPaper?.length < 1) return;
    let timer = null;
    if (
      generatedPaper.length >= 1 ||
      config.paperDiv ||
      config.fontSize ||
      config.spacing ||
      config.vocaFirstWordShow ||
      config.examKorShow ||
      config.orgLogoUrl
    ) {
      timer = setTimeout(() => {
        handleGeneratePdf();
      }, 100);
      return;
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tab,
    printSetTrigger,
    config.paperDiv,
    config.fontSize,
    config.spacing,
    config.vocaFirstWordShow,
    config.examKorShow,
    config.orgLogoUrl,
    generatedPaper,
  ]);

  // 저장한 시험지 최초 로딩시 가림막 없애기
  useEffect(() => {
    if (isLoadMode && pdfUrl !== null && generatedPaper?.length > 0) {
      setHideTestPaper(false);
    }
  }, [isLoadMode, pdfUrl, generatedPaper, setHideTestPaper]);

  // 교재이동하여 generatedPaper 초기화시 pdf 초기화
  useEffect(() => {
    if (generatedPaper?.length < 1) {
      setPdfUrl(null);
      setPdfBlob(null);
    }
  }, [generatedPaper, setPdfUrl, setPdfBlob]);

  // tab 변경, printSet 변경 시 로딩 표현
  useEffect(() => {
    if (pdfUrl === null) return;
    if (hideTestPaper) return;
    setViewModeChanging(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tab,
    printSetTrigger,
    config.paperDiv,
    config.fontSize,
    config.spacing,
    config.vocaFirstWordShow,
    config.examKorShow,
    config.orgLogoUrl,
  ]);

  return (
    <div className="relative flex pt-[18px] flex-col justify-start items-center bg-[#969ba4] h-full grow overflow-hidden">
      <div
        className="absolute top-0 bottom-0 left-0 right-0 flex flex-col justify-center items-center gap-[22px]"
        style={{
          display: hideTestPaper ? 'flex' : 'none',
          background: 'rgba(0,0,0, .6)',
          zIndex: 100,
        }}
      >
        <div
          className="flex flex-col justify-center items-center text-[#fff] text-[14px] font-[400]"
          style={{
            display:
              isLoadMode && pdfUrl === null && generatedPaper?.length > 0
                ? 'none'
                : 'flex',
          }}
        >
          <div>출제를 완료하시겠습니까?</div>
          <div>아래 버튼을 클릭하여 화면을 업데이트해 주세요.</div>
        </div>
        {isLoadMode && pdfUrl === null && generatedPaper?.length > 0 ? (
          <CircularProgress color="third" />
        ) : (
          <Button
            variant="contained"
            className="text-[14px] font-[700]"
            onClick={() => {
              saveFinalVocaList();
              setLoading(true);
              setHideTestPaper(false);
              setPrintSetOpen({ set: true, print: true });
              setIsFolding({ unit: true, type: true, voca: false });
            }}
            disabled={saveBtnDisabled}
            sx={{ fontFamily: 'Pretendard' }}
          >
            변경사항 업데이트
          </Button>
        )}
      </div>
      <div
        className="relative flex justify-center items-start"
        style={{ width: `${iframeWidth}px` }}
      >
        <VocaTestPaperSetBtns tab={tab} setTab={setTab} />
        <button
          type="button"
          className="absolute top-[6px] right-[0] z-50 flex items-center justify-center gap-1 border border-white py-2 px-3 rounded-[20px]"
          onClick={
            printSetOpen.set || printSetOpen.print
              ? () => {
                  setIsFolding({ unit: false, type: false, voca: false });
                  setPrintSetOpen({ set: false, print: false });
                }
              : () => {
                  setIsFolding({ unit: true, type: true, voca: false });
                  setPrintSetOpen({ set: true, print: true });
                }
          }
        >
          <div
            className="text-[#fff] text-[12px] mt-[1px]"
            style={{ fontFamily: 'Pretendard' }}
          >
            시험지 설정
          </div>
          <img src={setIcon} alt="folding" style={{}} />
        </button>
      </div>
      {loading ? (
        <div
          className="flex justify-center items-center bg-[#969ba4] rounded-xl"
          style={{ width: `${iframeWidth}px`, height: `${iframeHeight}px` }}
        >
          <CircularProgress color="inherit" />
        </div>
      ) : (
        <div
          className="relative bg-[#969ba4] overflow-hidden flex justify-center items-center"
          style={{
            width: `${iframeWidth}px`,
            height: `${iframeHeight}px`,
          }}
        >
          <div className="relative">
            {viewModeChanging && (
              <CircularProgress
                color="inherit"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '47%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1000,
                }}
              />
            )}
            {pdfUrl && !loading ? (
              <iframe
                // src={pdfUrl}
                src={`${pdfUrl}#toolbar=0`}
                title="PDF Preview"
                style={{
                  width: `${iframeWidth}px`,
                  height: `${iframeHeight}px`,
                  overflow: 'hidden',
                }}
              />
            ) : (
              <div
                style={{
                  width: `${iframeWidth}px`,
                  height: `${iframeHeight}px`,
                }}
              />
            )}
          </div>
        </div>
      )}
      <div
        className="grow flex flex-col justify-start items-center w-full overflow-y-auto"
        style={{ display: 'none' }}
      >
        <div
          ref={contentRef}
          style={{
            height: 'auto',
            overflow: 'visible',
            margin: '0 0 1.2cm',
            padding: '0 1.2px',
            width: '749px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          }}
        >
          <Header config={config} />
          <div
            style={{
              position: 'relative',
              columnCount: config?.paperDiv,
              columnGap: '20px',
              textAlign: 'justify',
              wordBreak: 'break-all',
              width: '100%',
              height: 'auto',
            }}
          >
            {tab !== PAPER_TABS.VOCABULARY_LIST && (
              <>
                {generatedPaper?.some(type => type.type === 'A') && (
                  <ATypeTestRows
                    vocaList={generatedPaper?.find(type => type.type === 'A')}
                    startNo={
                      generatedPaper?.find(type => type.type === 'A')?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
                {generatedPaper?.some(type => type.type === 'B') && (
                  <BTypeTestRows
                    vocaList={generatedPaper?.find(type => type.type === 'B')}
                    startNo={
                      generatedPaper?.find(type => type.type === 'B')?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
                {generatedPaper?.some(type => type.type === 'C') && (
                  <CTypeTestRows
                    vocaList={generatedPaper?.find(type => type.type === 'C')}
                    startNo={
                      generatedPaper?.find(type => type.type === 'C')?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
                {generatedPaper?.some(type => type.type === 'D') && (
                  <DTypeTestRows
                    vocaList={generatedPaper?.find(type => type.type === 'D')}
                    startNo={
                      generatedPaper?.find(type => type.type === 'D')?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
                {generatedPaper?.some(type => type.type === 'E') && (
                  <ETypeTestRows
                    vocaList={generatedPaper?.find(type => type.type === 'E')}
                    startNo={
                      generatedPaper?.find(type => type.type === 'E')?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
                {generatedPaper?.some(type => type.type === 'F') && (
                  <FTypeTestRows
                    vocaList={generatedPaper?.find(type => type.type === 'F')}
                    startNo={
                      generatedPaper?.find(type => type.type === 'F')?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
                {generatedPaper?.some(type => type.type === 'G') && (
                  <GTypeTestRows
                    vocaList={generatedPaper?.find(type => type.type === 'G')}
                    startNo={
                      generatedPaper?.find(type => type.type === 'G')?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
                {generatedPaper?.some(type => type.type === 'H') && (
                  <HTypeTestRows
                    vocaList={generatedPaper?.find(type => type.type === 'H')}
                    startNo={
                      generatedPaper?.find(type => type.type === 'H')?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
                {generatedPaper?.some(type => type.type === 'I') && (
                  <ITypeTestRows
                    vocaList={generatedPaper?.find(type => type.type === 'I')}
                    startNo={
                      generatedPaper?.find(type => type.type === 'I')?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
                {generatedPaper?.some(type => type.type === 'J') && (
                  <JTypeTestRows
                    vocaList={generatedPaper?.find(type => type.type === 'J')}
                    startNo={
                      generatedPaper?.find(type => type.type === 'J')?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
                {generatedPaper?.some(type => type.type === 'K') && (
                  <KTypeTestRows
                    vocaList={generatedPaper?.find(type => type.type === 'K')}
                    startNo={
                      generatedPaper?.find(type => type.type === 'K')?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
                {generatedPaper?.some(
                  type => type.type === 'L' && type.title?.includes('유의어'),
                ) && (
                  <LTypeTestRows
                    vocaList={generatedPaper?.find(
                      type =>
                        type.type === 'L' && type.title?.includes('유의어'),
                    )}
                    startNo={
                      generatedPaper?.find(
                        type =>
                          type.type === 'L' && type.title?.includes('유의어'),
                      )?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
                {generatedPaper?.some(
                  type => type.type === 'L' && type.title?.includes('반의어'),
                ) && (
                  <LTypeTestRows
                    vocaList={generatedPaper?.find(
                      type =>
                        type.type === 'L' && type.title?.includes('반의어'),
                    )}
                    startNo={
                      generatedPaper?.find(
                        type =>
                          type.type === 'L' && type.title?.includes('반의어'),
                      )?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
                {generatedPaper?.some(type => type.type === 'M') && (
                  <MTypeTestRows
                    vocaList={generatedPaper?.find(type => type.type === 'M')}
                    startNo={
                      generatedPaper?.find(type => type.type === 'M')?.startNo
                    }
                    tab={tab}
                    config={config}
                  />
                )}
              </>
            )}
            {tab === PAPER_TABS.VOCABULARY_LIST && (
              <CommonPaperVoca vocaList={commonPaperVocaList} config={config} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

VocaTestPaper.propTypes = {
  tab: PropTypes.string.isRequired,
  setTab: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  pdfUrl: PropTypes.string,
  setPdfUrl: PropTypes.func.isRequired,
  setPdfBlob: PropTypes.func.isRequired,
  setPrintSetOpen: PropTypes.func.isRequired,
  setIsFolding: PropTypes.func.isRequired,
  generatedPaper: PropTypes.array,
  hideTestPaper: PropTypes.bool.isRequired,
  setHideTestPaper: PropTypes.func.isRequired,
  saveFinalVocaList: PropTypes.func.isRequired,
  startNumbers: PropTypes.array,
  saveBtnDisabled: PropTypes.bool.isRequired,
  printSetTrigger: PropTypes.bool.isRequired,
  commonPaperVocaList: PropTypes.array,
  isLoadMode: PropTypes.bool.isRequired,
  printSetOpen: PropTypes.object.isRequired,
};

export default React.memo(VocaTestPaper);
